// DateUtils.ts
import { isDate, isNumber, isString } from "lodash";
import { DateDisplayFormat } from '../Enums/UiEnums';

// A date can be passed as any of these types.  Use toDate to convert to just a Date value
export type NoTzDate = string;
type DateStringOrNumber = Date | string | number | NoTzDate;

// Convert a date to a NoTzDate
export function toNoTzDate(date: Date|undefined)
{
    return date?.toLocaleDateString();
}

export function getNoTzToday(displayFormat?: DateDisplayFormat | undefined) {
    if (!displayFormat) {
        return new Date().toLocaleDateString();
    }
    return toDisplayDateString(new Date(), displayFormat) as NoTzDate;
} 
export function getNoTzDateAdd(number_of_days: number, displayFormat?: DateDisplayFormat | undefined) : NoTzDate{
    const today = new Date(getNoTzToday()); 
    const start_date = addDay(today, number_of_days); 
    if (!displayFormat) {
        return start_date.toLocaleDateString();
    }
    return toDisplayDateString(start_date, displayFormat) as NoTzDate;
  }

// Get the age between two dates.  If no zeroAgeDate value is passed, we use now 
export function getAge(_agingItem: DateStringOrNumber | undefined, _zeroAgeDate?: DateStringOrNumber): number | undefined
{
    const agingItem = toDate(_agingItem);
    if (!agingItem)
        return;

    if (!_zeroAgeDate)
        _zeroAgeDate = new Date();
    const zeroAgeDate = toDate(_zeroAgeDate);
    if (!zeroAgeDate)
        return;

    // Get the date difference
    const ageDifMs = zeroAgeDate.getTime() - agingItem.getTime();

    const ageDate = new Date(ageDifMs); // miliseconds from epoch
    return ageDate.getUTCFullYear() - 1970;    
}

// Generate a 'pretty' date string
export function prettyDate(date: DateStringOrNumber | undefined) : string | undefined {
    const validDate = toDate(date);
    if (!validDate)
        return;

    return `${monthAbbr(validDate)} ${validDate.getDate()}, ${validDate.getFullYear()}`;
}

// Convert a date value to the specified date string format
export function toDisplayDateString(dateVal: DateStringOrNumber | undefined, displayFormat: DateDisplayFormat = DateDisplayFormat.PrettyDate): string|undefined {
    if (undefined == dateVal)
        return;

    if (isString(dateVal))
        dateVal = new Date(dateVal);
    if (isNumber(dateVal))
        dateVal = new Date(dateVal);

    if (!isDate(dateVal))
    {
        alert("Invalid data type passed to toDisplayDate");
        return;
    }

    const strMonth = dateVal.toLocaleDateString("en-US", {month:"2-digit"});
    const strDay = dateVal.toLocaleDateString("en-US", {day:"2-digit"});

    switch(displayFormat)
    {
        case DateDisplayFormat.PrettyDate:
        return prettyDate(dateVal);

        case DateDisplayFormat.NumericDate:
        return `${strMonth}/${strDay}/${dateVal.getFullYear()}`;

        case DateDisplayFormat.NumericDashDate:
        return `${strMonth}-${strDay}-${dateVal.getFullYear()}`;

        case DateDisplayFormat.ISO8601:
        return `${dateVal.getFullYear()}-${strMonth}-${strDay}T${getTwoDigitValue(dateVal.getHours())}:${getTwoDigitValue(dateVal.getMinutes())}:${getTwoDigitValue(dateVal.getSeconds())}`;

        default:
            alert(`Unhandled DateDisplayFormat: ${displayFormat}`);
        return;
    }
}

function getTwoDigitValue(value?: number): string|undefined 
{
    return value?.toString().padStart(2, '0');
}

// Indicate what the date format should be
export function dateFormatterString(displayFormat: DateDisplayFormat = DateDisplayFormat.PrettyDate): string|undefined
{
    switch(displayFormat)
    {
        case DateDisplayFormat.PrettyDate:
            return "MM dd, yyyy";

        case DateDisplayFormat.NumericDate:
            return "MM/dd/yyyy";
        case DateDisplayFormat.NumericDashDate:
            return "MM-dd-yyyy";

        default:
            alert(`Unhandled DateDisplayFormat: ${displayFormat} for dateFormatterString`);
            return;
    }
}

// Generate a 'pretty' time string
export function prettyTime(date: DateStringOrNumber | undefined, suppressMidnight: boolean = false, militaryTime: boolean = false, includeTimeZone: boolean = false) : string | undefined {
    const validDate = toDate(date);
    if (!validDate)
        return;

    if (suppressMidnight ?? false)
    {
        if (true !== (isMidnight(validDate) ?? true))
            return;
    }

    return validDate.toLocaleTimeString("en-us", { hourCycle:  militaryTime ? "h24" : "h12", timeZoneName: includeTimeZone ? "short" : undefined });
}

export function prettyDateTime(date: DateStringOrNumber|Date|undefined): string | undefined
{
    const validDate = toDate(date);
    if (!validDate)
        return;

    return `${prettyDate(validDate)} ${prettyTime(validDate)}`;
}

export function todayDateAndTime(displayFormat?: DateDisplayFormat | undefined) : string | undefined {
    if (!displayFormat) {
        displayFormat = DateDisplayFormat.NumericDate;
    }
    return toDisplayDateString(new Date(), displayFormat) + " " + prettyTime(Date.now(), false, false, true);
}

// Convert a time into parts
export function getTimeStringParts(date: DateStringOrNumber, militaryTime: boolean = false): {time: string, ampm?: string, timeZoneAbbr: string } | undefined
{
    const validDate = toDate(date);
    if (!validDate)
        return;

    if (militaryTime)
    {
        const [time, timeZoneAbbr] = new Date(validDate).toLocaleTimeString('en-us',{timeZoneName:'short', hourCycle:  "h24" }).split(' '); 
        return {time, timeZoneAbbr};    
    }
    else
    {
        const [time, ampm, timeZoneAbbr] = new Date(validDate).toLocaleTimeString('en-us',{timeZoneName:'short', hourCycle: "h12" }).split(' '); 
        return {time, ampm, timeZoneAbbr};        
    }
}

// Is this time at midnight?
export function isMidnight(date: DateStringOrNumber): boolean | undefined
{
    const validDate = toDate(date);
    if (!validDate)
        return;

    const strTime = validDate.toLocaleTimeString("en-us", { hourCycle: "h12" });
    return "12:00:00 AM" === strTime;
}

export function toDate(date: DateStringOrNumber | undefined) : Date | undefined {
    if (!date)
        return;

    if ("string" === typeof(date))
    {
        const tempDate = Date.parse(date);
        if (!tempDate)
            return;
        return new Date(tempDate);
    }
    if ("number" === typeof(date))
    {
        return new Date(date);
    }
    return new Date(date);
}

// See if a string contains a valid date
export function isValidDate(date?: string|Date) : boolean
{
    if (!date)
        return false;

    if (isDate(date))
    {
        return "Invalid Date" != new Date(date).toString();
    }
    else if ("string" === typeof(date))
    {
        // Check the string
        if ("" === date.trim())
            return false;
        if ("Invalid Date" == date)    
            return false;
        if (Number.isNaN(new Date(date).getDate()))
            return false;
        return true;
    }

    return false;
}

//More robust date validation using RegEx and other tests
export function isValidDateRegEx(dateInput: any) {
    //Regex to check if the date format is MM/DD/YYYY and year is 19XX or 20XX
    const dateRegex = /^(0?[1-9]|1[012])\/(0?[1-9]|[12][0-9]|3[01])\/(19|20)?\d{2}$/;

    if (!dateRegex.test(dateInput)) {
        return false; 
    }

    const date = new Date(dateInput);
    const timestamp = date.getTime();

    //if date is valid, timestamp will be a number
    if (typeof timestamp !== 'number' || Number.isNaN(timestamp)) {
        return false; 
    }

    //Check if the date hasn't rolled over to a different date
    //The date parts (day and month) haven't inadvertently rolled over due to an invalid date like 02/30/2020, which 
    //JavaScript would typically convert to 03/01/2020.
    return date.getDate() == parseInt(dateInput.split('/')[1]) &&
            (date.getMonth() + 1) == parseInt(dateInput.split('/')[0]); 
    }

// Get the start of the day.  If nothing is passed we use today
export function startOfDay(date?: Date) {
    if (!date)
        date = new Date();

    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

// Get the start of the day.  If nothing is passed we use today
export function endOfDay(date?: Date) {
    if (!date)
        date = new Date();

    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);
}

// Get the start of the specified month
export function startOfMonth(date?: Date){
    if (!date)
        date = new Date();

    return new Date(date.getFullYear(), date.getMonth(), 1);
}

// Get the last second of the specified date's month
export function endOfMonth(date?: Date): Date {
    return addSeconds(addMonth(startOfMonth(date), 1), -1);
}

type Quarter = 1|2|3|4;

// Which quarter is the date in?
export function dateQuarter(date?: Date): Quarter {
    date = date ?? new Date();
    return Math.ceil((date.getMonth() + 1) / 3) as Quarter;
}

// Get the start of a quarter
export function startOfQuarter(year: number, quarter: Quarter): Date {
    return new Date(year, quarter * 3 - 3, 1);
}

// Get the end of a quarter
export function endOfQuarter(year: number, quarter: Quarter): Date {
    return new Date(year, quarter * 3, 0);
}

// Positive value shifts to the future, negative to the past.
export function addYear(date: Date, moveBy: number): Date {
    date.setFullYear(date.getFullYear() + moveBy);
    return date;
}

// Positive value shifts to the future, negative to the past.
export function addMonth(date: Date, moveBy: number): Date {
    date.setMonth(date.getMonth() + moveBy);
    return date;
}

export function addWeeks(date :  Date, numberOfWeeks: number) : Date {
    date.setDate(date.getDate() + numberOfWeeks * 7);
    return date;
} 

// Positive value shifts to the future, negative to the past.
export function addDay(date: Date, moveBy: number): Date {
    date.setDate(date.getDate() + moveBy);
    return date;
}

// Positive value shifts to the future, negative to the past.
export function addSeconds(date: Date, moveBy: number): Date{
    date.setSeconds(date.getSeconds() + moveBy);
    return date;
}

// Positive value shifts to the future, negative to the past.
export function addMinutes(date: Date, moveBy: number): Date{
    date.setSeconds(date.getSeconds() + 60*moveBy);
    return date;
}

// Get the month abbreviation
export function monthAbbr(date: Date): string {
    return date.toLocaleString("default", { month: "short" });
}

// Pad a value with a zero if less than 10
export function twoDigitTimeValue(val: number): string {
    return val < 10 ? "0" + val : "" + val;
}

export function getDateDiffInDays(startDate: Date, endDate: Date): number {
    const differenceInMs: number = endDate.getTime() - startDate.getTime();
    const msPerDay: number = 24 * 60 * 60 * 1000;
    const differenceInDays: number = Math.round(differenceInMs / msPerDay);
    return differenceInDays;
}


interface DateRange
{
    startDate: Date,
    endDate: Date
}

// Get the date range for a quarter based on a date
export function quarterRange(date?: Date) : DateRange
{
    date = date ?? new Date();
    const quarter = dateQuarter(date);
    const quarterYear = date.getFullYear();

    return { 
        startDate: startOfQuarter(quarterYear, quarter),
        endDate: endOfQuarter(quarterYear, quarter)
    }
}

// Get the prior quarter date range
export function lastQuarterRange(date?: Date) : DateRange
{
    date = date ?? new Date();
    const currentQuarter = dateQuarter(date);
    const lastQuarter  = (currentQuarter > 1 ?  currentQuarter - 1 : 1) as Quarter;
    const lastQuarterYear = (4 === lastQuarter) ? (date).getFullYear() -1: (date).getFullYear();

    return { 
        startDate: startOfQuarter(lastQuarterYear, lastQuarter),
        endDate: endOfQuarter(lastQuarterYear, lastQuarter)
    }
}

// Get the prior month date range
export function lastMonthRange(date?: Date) : DateRange
{
    date = date ?? new Date();
    const dayInPriorMonth = addMonth(date, -1);

    return {
        startDate: startOfMonth(dayInPriorMonth),
        endDate: endOfMonth(dayInPriorMonth)
    }
}

export function getShortDate(date: string, today?: string) : string {
    if (date.length == 0)
        return date;
        
    if (today) {
        if (date == today) {
            return "Today";
        }
    }
        
    const arr_date = date.split("/");
    return parseInt(arr_date[0], 10) + "/" + parseInt(arr_date[1], 10) + "/" + arr_date[2].substring(2,4);
}

// Check if dateValue is between the start and end date
export function isBetween(startDate: DateStringOrNumber | undefined, endDate: DateStringOrNumber | undefined, value: DateStringOrNumber | undefined): boolean {
    if (undefined === value)
        return false;
    return value >= (toDate(startDate) ?? new Date(3000, 0, 1)) || value <= (toDate(endDate) ?? new Date(1000, 0, 1));
}

interface DateParts {
    day: number, 
    month: number,  // Not an index value, is the month.  Jan = 1
    fullYear: number
}

// Convert a date to date parts
//  Jan => 1, not zero
export function getDateParts(dateString?: string, displayFormat?: DateDisplayFormat): DateParts|undefined
{
    if (!dateString)
        return;

    switch(displayFormat)
    {
        case DateDisplayFormat.NumericDate:
            return getDatePartsForNumericSlashDate(dateString);
        case DateDisplayFormat.NumericDashDate:
            return getDatePartsForNumericDashDate(dateString);
        case DateDisplayFormat.PrettyDate:
            return getDatePartsForPrettyDate(dateString);

        // Try to determine what the date format is
        case undefined:
            if (10 == dateString.indexOf("T"))
                return getDatePartsForXmlDate(dateString);
            if (dateString.indexOf("-") > 0)
                return getDatePartsForNumericDashDate(dateString);
            if (dateString.indexOf("/") > 0)
                return getDatePartsForNumericSlashDate(dateString);
            if (dateString.indexOf(" ") > 0 )
                return getDatePartsForPrettyDate(dateString);

            if (8 === dateString.length)
                return getDatePartsForNumericString(dateString)
            break;

        default:
            alert(`Unhandled displayFormat: ${displayFormat} calling getDateParts()`);
    }
}

// Parse slash format: '7/4/1776'
export function getDatePartsForNumericSlashDate(dateString?: string): DateParts|undefined
{
    const parts = dateString?.split("/");
    if (3 != parts?.length)
        return;
    return { fullYear: +parts[2], month: +parts[0], day: +parts[1]}
}

// Parse XML date format: YYYY-MM-DDTHH:mm:ss
export function getDatePartsForXmlDate(dateString?: string): DateParts|undefined
{
    const dateAndTimeParts = dateString?.split("T");
    if (2 != dateAndTimeParts?.length)
        return;

    const parts = dateAndTimeParts[0].split("-");
    if (3 != parts?.length)
        return;
    return { fullYear: +parts[0], month: +parts[1], day: +parts[2]}
}

// Parse dash format: '7-4-1776'
export function getDatePartsForNumericDashDate(dateString?: string): DateParts|undefined
{
    const parts = dateString?.split("-");
    if (3 != parts?.length)
        return;
    return { fullYear: +parts[2], month: +parts[0], day: +parts[1]}
}

// Parse Pretty format: 'Jul 4, 1776'
export function getDatePartsForPrettyDate(dateString?: string): DateParts|undefined
{
    if (!dateString || 0 === dateString.length)
        return;

    // Parse the date to get the month name
    const dateVal = new Date(dateString);

    // Parse the date string to get the year
    const parts = dateString.match(/(?<MonthName>\S*)\s*(?<Day>\d*)\s*,?\s*(?<Year>\d*)/);
    const year = parts?.groups?.Year;
    if (undefined === year || 0 === year.trim().length)
        return;

    return { month: dateVal.getMonth()+1, day: dateVal.getDate(), fullYear: +year};
}

// Parse numeric format: '07041776'
export function getDatePartsForNumericString(dateString?: string): DateParts|undefined
{
    if (!dateString)
        return;

    if (8 !== dateString.length)
        return;

    const numericOnlyDate = dateString.match(/(?<Month>\d{2})(?<Day>\d{2})(?<Year>\d{4})/);
    if (!numericOnlyDate)
        return;
    // Any nulls?
    if (undefined === numericOnlyDate.groups?.Year
        || undefined === numericOnlyDate.groups?.Month
        || undefined === numericOnlyDate.groups?.Day)
        return;
    // Empty strings?
    if (!numericOnlyDate.groups.Year.length
        || !numericOnlyDate.groups.Month.length
        || !numericOnlyDate.groups?.Day.length)
        return;

    return { fullYear: +numericOnlyDate.groups.Year, month: +numericOnlyDate.groups.Month, day: +numericOnlyDate.groups.Day };
}


// Convert date parts into a date.
export function dateFromParts(date?: DateParts): Date|undefined
{
    if (!date)
        return;

    const newDate = new Date(date.fullYear, date.month-1, date.day);
    if (date.fullYear < 100)
        newDate.setFullYear(date.fullYear);

    return newDate;
}

// Split a string into date & time parts
export function splitIntoDateAndTimeParts(dateTimeString?: NoTzDate): ISplitDateTime|undefined {
    if (undefined === dateTimeString)
        return;

    const timePart = dateTimeString?.toLowerCase().match(/(?<Time>\d{1,2}:\d{1,2}:\d{1,2})/gm);
    if (!timePart?.length)
        return;

    const time = timePart[0];
    const timeParts = time.split(":");

    const ampmPart = dateTimeString?.toLowerCase().match(/(?<AMPM>am|pm)/gm);
    const ampm = ampmPart?.length ? ampmPart[0] : "";

    const dateStr = dateTimeString.replace(time, "").replace(ampm, "").trim();

    let hours, minutes, seconds;
    if (timeParts.length > 0)
        hours = +timeParts[0];
    if (timeParts.length > 1)
        minutes = +timeParts[1];
    if (timeParts.length > 2)
        seconds = +timeParts[2];

    let am: boolean|undefined;
    if ("am" === ampm)
        am = true;
    if ("pm" === ampm)
        am = false;

    return {
        dateNoTz: dateStr,
        hours: hours ?? 0,
        minutes: minutes ?? 0,
        seconds: seconds ?? 0,
        am:am
    };
}

export interface ISplitDateTime
{
    dateNoTz: NoTzDate,
    hours: number,
    minutes: number,
    seconds: number,
    am?:boolean         // Null for military, true for AM, false for PM
}

export function isDateLessThanOrEqualTo(date1: DateStringOrNumber, date2: DateStringOrNumber): boolean {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1 <= d2;
}

// See if a date is between a start and end date.  If the start or end isn't provided we assume a very early/late date which can be altered via the options value
export function isBetweenDates(
    dateVal?: DateStringOrNumber, 
    startDate?: DateStringOrNumber, 
    endDate?: DateStringOrNumber, 
    options: {defaultStartDate: Date, defaultEndDate: Date} = {defaultStartDate: new Date(1900, 1, 1), defaultEndDate: new Date(2100, 1, 1, 23, 23, 59)}): boolean
{
    const dateObj = toDate(dateVal);
    if (!dateObj)
        return false;


    const startDateObj = toDate(startDate) ?? options.defaultStartDate;
    const endDateObj = endOfDay(toDate(endDate)) ?? options.defaultEndDate;

    if (dateObj >= startDateObj && dateObj <= endDateObj)
        return true;

    return false;
}